import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMyStats,
  selectMyStats,
  selectMyStatsLoading,
  selectMyStatsError,
  clearError
} from '../store/slices/userStatsSlice';
import {
  UserCircleIcon,
  EnvelopeIcon,
  CalendarIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  FlagIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';

const Profile = () => {
  const dispatch = useDispatch();
  const myStats = useSelector(selectMyStats);
  const loading = useSelector(selectMyStatsLoading);
  const error = useSelector(selectMyStatsError);

  useEffect(() => {
    dispatch(fetchMyStats());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchMyStats());
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      superadmin: 'from-red-500 to-red-600',
      global_admin_hq: 'from-purple-500 to-purple-600',
      country_director: 'from-blue-500 to-blue-600',
      institutional_relations_officer: 'from-green-500 to-emerald-600',
      legal_compliance_team: 'from-yellow-500 to-amber-600',
      read_only_auditor: 'from-gray-500 to-gray-600'
    };
    return colors[role] || 'from-gray-500 to-gray-600';
  };

  const getRoleDisplayName = (role) => {
    const names = {
      superadmin: 'Super Admin',
      global_admin_hq: 'Global Admin (HQ)',
      country_director: 'Country Director',
      institutional_relations_officer: 'Institutional Relations Officer',
      legal_compliance_team: 'Legal Compliance Team',
      read_only_auditor: 'Read-Only Auditor'
    };
    return names[role] || role;
  };

  if (loading && !myStats) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="space-y-4">
              <div className="h-6 bg-gray-200 rounded"></div>
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => dispatch(clearError())}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!myStats) {
    return null;
  }

  const { user, statistics, recentContacts } = myStats;
  const institutionTypeEntries = Object.entries(statistics.institutionTypeBreakdown || {}).sort((a, b) => b[1] - a[1]);
  const countryEntries = Object.entries(statistics.countryBreakdown || {}).sort((a, b) => b[1] - a[1]);
  const priorityEntries = Object.entries(statistics.priorityBreakdown || {}).sort((a, b) => b[1] - a[1]);
  const relationshipStageEntries = Object.entries(statistics.relationshipStageBreakdown || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            My Profile
          </h1>
          <p className="mt-2 text-base text-gray-600 font-medium">
            View your contact statistics and activity
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 shadow-md"
        >
          <ArrowPathIcon className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* User Info Card */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200">
        <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 text-center sm:text-left">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-4 rounded-2xl shadow-lg flex-shrink-0">
            <UserCircleIcon className="h-16 w-16 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 truncate">{user.name}</h2>
            <div className="flex items-center justify-center sm:justify-start space-x-2 mb-3">
              <EnvelopeIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <span className="text-gray-600 truncate">{user.email}</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r ${getRoleBadgeColor(user.role)} text-white shadow-md`}>
                <CheckBadgeIcon className="h-5 w-5 mr-2" />
                {getRoleDisplayName(user.role)}
              </span>
              <div className="flex items-center text-sm text-gray-500 whitespace-nowrap">
                <CalendarIcon className="h-4 w-4 mr-1" />
                Joined {formatDate(user.createdAt)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 border border-blue-100 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer group">
          <div className="flex flex-col items-center">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <ChartBarIcon className="h-8 w-8 text-white" />
            </div>
            <span className="text-4xl font-bold text-gray-900 mb-1">{statistics.totalContacts}</span>
            <p className="text-sm text-gray-600 font-medium">Total Contacts</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer group">
          <div className="flex flex-col items-center">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <BuildingOfficeIcon className="h-8 w-8 text-white" />
            </div>
            <span className="text-4xl font-bold text-gray-900 mb-1">{institutionTypeEntries.length}</span>
            <p className="text-sm text-gray-600 font-medium">Institution Types</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-green-100 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer group">
          <div className="flex flex-col items-center">
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <GlobeAltIcon className="h-8 w-8 text-white" />
            </div>
            <span className="text-4xl font-bold text-gray-900 mb-1">{countryEntries.length}</span>
            <p className="text-sm text-gray-600 font-medium">Countries</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-amber-100 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer group">
          <div className="flex flex-col items-center">
            <div className="bg-gradient-to-br from-amber-500 to-yellow-600 p-4 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <CalendarIcon className="h-8 w-8 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-900 mb-1">{formatDate(statistics.firstContactDate)}</span>
            <p className="text-sm text-gray-600 font-medium">First Contact</p>
          </div>
        </div>
      </div>

      {/* Institution Type Breakdown */}
      {institutionTypeEntries.length > 0 && (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-6">
            Contacts by Institution Type
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {institutionTypeEntries.map(([type, count]) => (
              <div key={type} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 mb-1">{type}</p>
                    <p className="text-xs text-gray-500">contacts</p>
                  </div>
                  <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Country Breakdown */}
      {countryEntries.length > 0 && (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-6 flex items-center">
            <FlagIcon className="h-6 w-6 mr-2 text-green-600" />
            Contacts by Country
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {countryEntries.map(([country, count]) => (
              <div key={country} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-green-300 hover:shadow-lg transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">{country}</p>
                  <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Priority & Relationship Stage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority Breakdown */}
        {priorityEntries.length > 0 && (
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Priority Breakdown</h3>
            <div className="space-y-3">
              {priorityEntries.map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200">
                  <span className="text-sm font-medium text-gray-700">{priority}</span>
                  <span className="text-lg font-bold text-blue-600">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Relationship Stage Breakdown */}
        {relationshipStageEntries.length > 0 && (
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Relationship Stage</h3>
            <div className="space-y-3">
              {relationshipStageEntries.map(([stage, count]) => (
                <div key={stage} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200">
                  <span className="text-sm font-medium text-gray-700">{stage}</span>
                  <span className="text-lg font-bold text-purple-600">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recent Contacts */}
      {recentContacts && recentContacts.length > 0 && (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
            Recent Contacts Added
          </h3>
          <div className="space-y-3">
            {recentContacts.map((contact) => (
              <div key={contact.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-900 truncate">
                    {contact.primaryName || contact.name || 'Unnamed Contact'}
                  </p>
                  <div className="flex items-center space-x-3 mt-1">
                    {contact.institutionType && (
                      <span className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
                        {contact.institutionType}
                      </span>
                    )}
                    {contact.country && (
                      <span className="text-xs text-gray-500 bg-green-50 px-2 py-1 rounded">
                        {contact.country}
                      </span>
                    )}
                    {contact.priority && (
                      <span className="text-xs text-gray-500 bg-amber-50 px-2 py-1 rounded">
                        {contact.priority}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right ml-4">
                  <p className="text-xs text-gray-500">{formatDate(contact.createdAt)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
